import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import AppShell from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { UploadCloud, FileText, Loader2, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";

async function waitForParse(id) {
  for (let i = 0; i < 240; i++) {
    await new Promise((res) => setTimeout(res, 3000));
    try {
      const { data } = await api.get(`/reports/${id}`);
      if (data.parse_status !== "processing") return data;
    } catch { /* transient; keep polling */ }
  }
  return { id, parse_status: "failed" };
}

export default function Upload() {
  const nav = useNavigate();
  const [consent, setConsent] = useState(false);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reports, setReports] = useState([]);
  const [stage, setStage] = useState("");

  useEffect(() => { api.get("/reports").then(r => setReports(r.data)).catch(()=>{}); }, []);

  const deleteReport = async (id) => {
    if (!window.confirm("Permanently delete this report?")) return;
    await api.delete(`/reports/${id}`);
    toast.success("Report deleted");
    setReports((rs) => rs.filter((r) => r.id !== id));
  };

  const onDrop = useCallback((e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  }, []);

  const upload = async () => {
    if (!consent) return toast.error("Please provide consent before uploading");
    if (!file) return toast.error("Choose a file");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      setStage("Uploading…");
      const r = await api.post("/reports/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setStage("Reading your report… this can take a few minutes");
      const report = await waitForParse(r.data.id);
      if (report.parse_status === "failed") {
        toast.error("We couldn't read this report. Try a text-based PDF or another file.");
        setReports((rs) => [report, ...rs.filter((x) => x.id !== report.id)]);
        return;
      }
      toast.success("Report uploaded & parsed");
      nav(`/app/review/${report.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); setStage(""); }
  };

  const loadSample = async () => {
    if (!consent) return toast.error("Please provide consent to continue");
    setBusy(true);
    try {
      const r = await api.post("/reports/sample");
      toast.success("Sample report loaded");
      nav(`/app/review/${r.data.id}`);
    } catch { toast.error("Could not load sample"); }
    finally { setBusy(false); }
  };

  return (
    <AppShell>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div>
            <div className="eyebrow">Step 1 · Upload</div>
            <h1 className="text-3xl font-semibold text-slate-900 mt-1">Upload Your Credit Report</h1>
            <p className="text-slate-500 mt-2 max-w-2xl">Drag & drop your PDF, DOCX, TXT, or image. We accept reports from Credit Hero, IdentityIQ, MyScoreIQ, SmartCredit, AnnualCreditReport.com, Experian, Equifax, TransUnion and other credit-monitoring services.</p>
          </div>

          <Card className="glass rounded-2xl">
            <CardContent className="p-6 sm:p-8">
              <label
                data-testid="upload-drag-drop-zone"
                onDragOver={(e)=>{e.preventDefault(); setDragOver(true);}}
                onDragLeave={()=>setDragOver(false)}
                onDrop={onDrop}
                className={`block cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition
                  ${dragOver ? "border-purple-400 bg-purple-400/5" : "border-slate-200 hover:border-purple-400/40 hover:bg-slate-50/70"}`}
              >
                <input type="file" className="hidden" accept=".pdf,.docx,.txt,image/*" onChange={(e)=>setFile(e.target.files?.[0]||null)} data-testid="upload-file-input" />
                <UploadCloud className="mx-auto text-purple-700" size={36}/>
                <div className="mt-3 text-slate-900 font-semibold">Drop your credit report here</div>
                <div className="text-sm text-slate-500 mt-1">PDF · DOCX · TXT · PNG · JPG — up to 20 MB</div>
                {file && (
                  <div className="mt-4 inline-flex items-center gap-2 rounded-xl bg-slate-50 border border-slate-200 px-3 py-1.5 text-sm text-slate-800" data-testid="upload-selected-file">
                    <FileText size={14}/> {file.name}
                  </div>
                )}
              </label>

              <div className="mt-6 flex items-start gap-3">
                <Checkbox id="consent" checked={consent} onCheckedChange={(v)=>setConsent(!!v)} data-testid="upload-consent-checkbox" />
                <label htmlFor="consent" className="text-sm text-slate-700 leading-relaxed">
                  I consent to secure processing of my credit report solely to generate dispute documents for my own use. My report will <b>never</b> be used to train AI models and remains private to my account.
                </label>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <Button onClick={upload} disabled={busy || !file || !consent} data-testid="upload-submit-button" className="btn-primary rounded-xl px-6">
                  {busy ? <><Loader2 className="animate-spin mr-2" size={16}/>Processing…</> : "Upload & Parse Report"}
                </Button>
                {stage && <div className="w-full text-sm text-purple-800" data-testid="upload-progress-status">{stage}</div>}
                <Button variant="outline" onClick={loadSample} disabled={busy || !consent} data-testid="upload-sample-button" className="rounded-xl border-slate-200 bg-slate-50/70 hover:bg-slate-50 text-slate-800">
                  Load Sample Report
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="glass rounded-2xl">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck size={16} className="text-purple-700"/>
                <div className="text-sm text-slate-900 font-medium">Privacy notice</div>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Files are encrypted in transit & storage, isolated per account, and never publicly accessible.
                Account & SSN numbers are automatically masked when displayed. You can permanently delete a report at any time.
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <div className="eyebrow">Your reports</div>
          {reports.length === 0 && <div className="text-slate-400 text-sm">No reports uploaded yet.</div>}
          {reports.map(r=>(
            <Card key={r.id} className="glass rounded-2xl hover-lift">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-slate-900 text-sm font-medium">{r.original_filename || "Report"}</div>
                    <div className="text-xs text-slate-500">{new Date(r.created_at).toLocaleString()} · {r.parse_status === "processing" ? "Processing…" : r.parse_status === "failed" ? "Could not be read" : `${r.accounts?.length || 0} accounts`}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" data-testid={`upload-open-report-${r.id}`} onClick={()=>nav(`/app/review/${r.id}`)} className="text-purple-700 hover:text-purple-700">Open</Button>
                    <Button size="sm" variant="ghost" aria-label="Delete report" data-testid={`upload-delete-report-${r.id}`} onClick={()=>deleteReport(r.id)} className="text-red-400 hover:text-red-600 min-h-[44px] min-w-[44px]"><Trash2 size={14}/></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
